import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import focusService from '../../services/focusService';
import settingsService from '../../services/settingsService';
import { Check, X, Edit2, Trash2, Star, Image as ImageIcon } from 'lucide-react';

const FocusControlsSection = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [focuses, setFocuses] = useState([]);
  const [primaryFocusId, setPrimaryFocusId] = useState(null);
  const [editingFocus, setEditingFocus] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadFocuses();
  }, [user]);

  const loadFocuses = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const [focusData, profile] = await Promise.all([
        focusService.getUserFocuses(user.id),
        settingsService.getUserProfile(user.id)
      ]);
      
      setFocuses(focusData);
      setPrimaryFocusId(profile?.primary_focus_id);
    } catch (error) {
      console.error('Error loading focuses:', error);
      setMessage({ type: 'error', text: 'Failed to load focuses' });
    } finally {
      setLoading(false);
    }
  };

  const handleRename = async (focusId, newTitle) => {
    if (!newTitle.trim()) return;
    
    try {
      await focusService.renameFocus(focusId, newTitle);
      await loadFocuses();
      setEditingFocus(null);
      setMessage({ type: 'success', text: 'Focus renamed successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error renaming focus:', error);
      setMessage({ type: 'error', text: 'Failed to rename focus' });
    }
  };

  const handleDelete = async (focusId, focusTitle) => {
    if (!confirm(`Are you sure you want to delete "${focusTitle}"? This cannot be undone.`)) return;
    
    try {
      await focusService.deleteFocus(focusId);
      await loadFocuses();
      setMessage({ type: 'success', text: 'Focus deleted successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error deleting focus:', error);
      setMessage({ type: 'error', text: 'Failed to delete focus' });
    }
  };

  const handleSetPrimary = async (focusId) => {
    try {
      await focusService.setPrimaryFocus(user.id, focusId);
      setPrimaryFocusId(focusId);
      setMessage({ type: 'success', text: 'Primary focus updated' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error setting primary focus:', error);
      setMessage({ type: 'error', text: 'Failed to set primary focus' });
    }
  };

  const handleToggleAutoSave = async (focusId, currentValue) => {
    try {
      await focusService.toggleAutoSaveConversations(focusId, !currentValue);
      await loadFocuses();
    } catch (error) {
      console.error('Error toggling auto-save:', error);
      setMessage({ type: 'error', text: 'Failed to update auto-save setting' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Focus Controls</h2>
        <p className="text-gray-600 mt-1">
          Manage your focuses and their settings
        </p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`flex items-center space-x-2 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? <Check size={20} /> : <X size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Focuses List */}
      {focuses.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-6xl mb-4">🎯</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Focuses Yet
          </h3>
          <p className="text-gray-600">
            Create your first focus from the Focus page
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {focuses.map(focus => {
            const isPrimary = focus.id === primaryFocusId;
            const isEditing = editingFocus === focus.id;

            return (
              <div
                key={focus.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    {/* Cover Image */}
                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-purple-400 to-blue-500 flex items-center justify-center text-white text-xl flex-shrink-0">
                      {focus.cover_image_url ? (
                        <img
                          src={focus.cover_image_url}
                          alt={focus.title}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <span>{focus.title?.[0] || '🎯'}</span>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Title */}
                      {isEditing ? (
                        <input
                          type="text"
                          defaultValue={focus.title}
                          onBlur={(e) => handleRename(focus.id, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleRename(focus.id, e.target.value);
                            } else if (e.key === 'Escape') {
                              setEditingFocus(null);
                            }
                          }}
                          autoFocus
                          className="text-lg font-semibold text-gray-900 border-b-2 border-purple-500 focus:outline-none w-full"
                        />
                      ) : (
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {focus.title}
                          </h3>
                          {isPrimary && (
                            <span className="flex items-center space-x-1 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full flex-shrink-0">
                              <Star size={12} fill="currentColor" />
                              <span>Primary</span>
                            </span>
                          )}
                        </div>
                      )}

                      {/* Description */}
                      {focus.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {focus.description}
                        </p>
                      )}

                      {/* Auto-save toggle */}
                      <div className="flex items-center space-x-2 mt-3">
                        <input
                          type="checkbox"
                          id={`autosave-${focus.id}`}
                          checked={focus.auto_save_conversations ?? true}
                          onChange={() => handleToggleAutoSave(focus.id, focus.auto_save_conversations)}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <label
                          htmlFor={`autosave-${focus.id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          Auto-save conversations
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 ml-4">
                    {!isPrimary && (
                      <button
                        onClick={() => handleSetPrimary(focus.id)}
                        className="p-2 text-gray-400 hover:text-yellow-500 transition-colors"
                        title="Set as primary"
                      >
                        <Star size={18} />
                      </button>
                    )}
                    <button
                      onClick={() => setEditingFocus(focus.id)}
                      className="p-2 text-gray-400 hover:text-blue-500 transition-colors"
                      title="Rename"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(focus.id, focus.title)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">
          About Focus Controls
        </h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Set a primary focus to be your default workspace</li>
          <li>• Auto-save conversations to keep your focus organized</li>
          <li>• Rename focuses to better describe their purpose</li>
          <li>• Delete focuses you no longer need</li>
        </ul>
      </div>
    </div>
  );
};

export default FocusControlsSection;





